/**
 * paymentRenderAdapters (T35.7.2.1) — the processor-agnostic client handoff.
 *
 * After `/session` returns a descriptor, the client mounts a render adapter
 * keyed by `descriptor.primary.processor`. The adapter owns the processor's
 * client-side checkout (Stripe Elements .2 / PayPal SDK .3); for V1 only the
 * stub adapter exists — it confirms the (gated) session synchronously.
 *
 * The "pay another way" switch is shown ONLY when `descriptor.alternatives` is
 * non-empty (dormant with one registered processor). Selecting an alternative
 * re-requests `/session` with that key — lazy, one session per chosen
 * processor. The client never names a processor in V1 (the alternatives carry a
 * neutral label from the server).
 *
 * Token-only (CP5); mobile-first; ≥44px targets.
 */

import { type CSSProperties, useEffect, useState } from 'react';
import type { ProcessorKey, SessionDescriptor } from './api';
import { StripePaymentElement } from './StripePaymentElement';
import { StripeNativePaymentSheet } from './StripeNativePaymentSheet';
import { probeStripeNativeReady } from '../../adapters/stripeNative';

const altWrapStyle: CSSProperties = {
  marginTop: 'var(--space-3)',
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--space-2)',
};

const altBtnStyle: CSSProperties = {
  border: 0,
  background: 'none',
  padding: 0,
  color: 'var(--color-accent-primary)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-bold)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
  minHeight: '2.75rem',
};

// "Or pay with card" divider — separates the native Google Pay button (above)
// from the always-rendered web card form (below) so the wallet reads as the
// PRIMARY action and the card form as the labelled fallback. Token-only (CP5);
// mirrors the "or" divider on the login card (LoginPage.tsx orDividerRowStyle).
const cardDividerRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-3)',
  marginTop: 'var(--space-3)',
  marginBottom: 'var(--space-3)',
  color: 'var(--color-text-secondary)',
  fontSize: 'var(--font-size-sm)',
  fontFamily: 'var(--font-family-sans)',
};

const cardDividerLineStyle: CSSProperties = {
  flex: 1,
  height: 'var(--border-width-hairline)',
  backgroundColor: 'var(--color-border-subtle)',
};

/** Props every render adapter receives (the descriptor + the result callback). */
interface RenderAdapterProps {
  descriptor: SessionDescriptor;
  /**
   * Reports the processor outcome up: `true` = charged (success). On `false` the
   * optional `reason` carries the processor's specific failure detail (e.g.
   * Stripe's "Your card was declined.") so the dialog can show it.
   */
  onResult?: ((ok: boolean, reason?: string) => void) | undefined;
}

/** The render adapter for a single processor. V1 ships the stub adapter only. */
function StubRenderAdapter({ descriptor }: RenderAdapterProps): JSX.Element {
  // The gated stub has no real checkout — the session is already finalized
  // server-side. The adapter renders the confirmation (no card fields). A real
  // adapter (.2/.3) mounts its SDK here keyed off `descriptor.primary.sessionToken`.
  return (
    <div data-testid="render-adapter-stub" data-session-token={descriptor.primary.sessionToken ?? ''}>
      {/* No card fields — real charging is gated; the session is recorded intent. */}
    </div>
  );
}

/**
 * The Stripe render adapter — keyed by `descriptor.processor === 'stripe'`. The
 * registry keys ONLY on the processor; this wrapper composes the client surfaces
 * INSIDE the Stripe path (T35.7.2.2 native checkout, per ADR-016 § Client
 * checkout surface by platform).
 *
 * The web `<PaymentElement>` (`StripePaymentElement`) is the ALWAYS-RENDERED
 * floor: it is pure Stripe.js (works in the Android System WebView for card
 * entry) and renders on EVERY platform and regardless of the probe outcome —
 * immediately, while probing, on web / PWA, on probe failure, and after a native
 * wallet cancel or failure. It is never gated or replaced.
 *
 * Native Google/Apple Pay is ADDITIVE: when the async `probeStripeNativeReady()`
 * proves the native wallet can genuinely run on this device (plugin compiled in,
 * SDK initialized, Google or Apple Pay provisioned), the OS-wallet "Pay" button
 * (`StripeNativePaymentSheet`) is shown ABOVE the card form as a faster option —
 * never as a substitute for it. Cancelling or failing the wallet leaves the user
 * on the working card form below, with no dead-end. Both surfaces charge against
 * the SAME `descriptor.primary.sessionToken` and finalize from the SAME webhook.
 *
 * `probeReady` is injectable so the RTL test drives both branches with no device.
 */
function StripeRenderAdapter({
  descriptor,
  onResult,
  probeReady = probeStripeNativeReady,
}: RenderAdapterProps & {
  /** Capability-probe override (tests inject a fake); defaults to the real seam probe. */
  probeReady?: () => Promise<boolean>;
}): JSX.Element {
  // The web Payment Element is always rendered below; an affirmative probe only
  // ADDS the native wallet button above it — it never replaces the card form, so
  // a wrong/over-eager probe (or a cancelled/failed wallet) can't strand the user.
  const [nativeReady, setNativeReady] = useState(false);

  useEffect(() => {
    let active = true;
    void probeReady().then((ready) => {
      if (active && ready) setNativeReady(true);
    });
    return () => {
      active = false;
    };
  }, [probeReady]);

  return (
    <>
      {nativeReady && (
        <>
          <StripeNativePaymentSheet descriptor={descriptor} onResult={onResult} />
          {/* Separate the Google Pay button (PRIMARY) from the card form below.
              Only present when the wallet is shown — on web / PWA / probe-false
              the card form is the sole surface and needs no divider. */}
          <div style={cardDividerRowStyle} data-testid="stripe-card-divider" aria-hidden="true">
            <span style={cardDividerLineStyle} />
            <span>Or pay with card</span>
            <span style={cardDividerLineStyle} />
          </div>
        </>
      )}
      <StripePaymentElement descriptor={descriptor} onResult={onResult} />
    </>
  );
}

const RENDER_ADAPTERS: Partial<Record<ProcessorKey, (props: RenderAdapterProps) => JSX.Element>> = {
  stub: StubRenderAdapter,
  // T35.7.2.2 — the Stripe surface, keyed by `descriptor.processor === 'stripe'`.
  // The element-vs-native-sheet platform fork happens inside StripeRenderAdapter.
  stripe: StripeRenderAdapter,
};

/**
 * Mount the render adapter for the descriptor's primary processor + the dormant
 * "pay another way" switch (shown only when alternatives is non-empty).
 * `onSelectAlternative` re-requests a session for the chosen key (the parent
 * owns the lazy re-fetch).
 */
export function PaymentHandoff({
  descriptor,
  onSelectAlternative,
  onResult,
}: {
  descriptor: SessionDescriptor;
  onSelectAlternative?: (processor: ProcessorKey) => void;
  /**
   * Reports the primary adapter's outcome up (`true` = charged/success). On
   * `false` the optional `reason` carries the processor's specific failure detail.
   */
  onResult?: ((ok: boolean, reason?: string) => void) | undefined;
}): JSX.Element {
  const Adapter = RENDER_ADAPTERS[descriptor.primary.processor] ?? StubRenderAdapter;
  const hasAlternatives = descriptor.alternatives.length > 0;

  return (
    <div data-testid="payment-handoff">
      <Adapter descriptor={descriptor} onResult={onResult} />

      {hasAlternatives && (
        <div style={altWrapStyle} data-testid="pay-another-way">
          {descriptor.alternatives.map((alt) => (
            <button
              key={alt.processor}
              type="button"
              style={altBtnStyle}
              data-testid={`pay-another-${alt.processor}`}
              onClick={() => onSelectAlternative?.(alt.processor)}
            >
              Pay another way: {alt.label} ›
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
